import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  useGetBalanceQuery,
  useBalanceSubscription,
  BalanceResponse,
} from '../generated/graphql';
import { useStationAuth } from './StationAuthContext';

// ----------------------------------------------------------------------
interface BalanceContextType {
  balance: {
    entry: string | undefined;
    winnings: string | undefined;
    redeemableWinnings: string | undefined;
    candy: string | undefined;
    usedCandy: string | undefined;
    candyToNextLevel: string | undefined;
    cashout: string | undefined;
    total: string | undefined;
  };
  refreshBalance: () => void;
}
const BalanceContext = createContext<BalanceContextType | undefined>(undefined);

type BalanceProviderProps = {
  children: ReactNode;
};

let maxSubscriptionConnectionRetries = 0;

export const BalanceProvider = ({ children }: BalanceProviderProps) => {
  const { playerId } = useStationAuth();
  const [balance, setBalance] = useState<BalanceResponse | null>(null);

  const [
    { data: getBalanceSubscriptionResults, error: getBalanceSubscriptionError },
    startBalanceSubscription,
  ] = useBalanceSubscription({
    variables: {
      playerId: playerId!,
    },
    pause: playerId == null,
  });

  useEffect(() => {
    if (getBalanceSubscriptionError) {
      console.error(getBalanceSubscriptionError);
    }
  }, [getBalanceSubscriptionError]);

  const [{ data: queryData }, refetchBalance] = useGetBalanceQuery({
    variables: { playerId: playerId! },
    pause: true,
  });

  useEffect(() => {
    const subBalance = getBalanceSubscriptionResults?.balance;

    if (
      subBalance === undefined &&
      playerId &&
      maxSubscriptionConnectionRetries < 3
    ) {
      maxSubscriptionConnectionRetries++;
      startBalanceSubscription();
    } else if (subBalance !== undefined) {
      maxSubscriptionConnectionRetries = 0;
    }

    if (subBalance) {
      setBalance(subBalance);
    }
  }, [getBalanceSubscriptionResults, getBalanceSubscriptionError]);

  const refreshBalance = async () => {
    if (playerId) {
      await refetchBalance();
      if (queryData?.getBalance) {
        setBalance(queryData.getBalance);
      }
    }
  };

  return (
    <BalanceContext.Provider
      value={{
        balance: {
          entry: balance?.entry,
          winnings: balance?.winnings,
          redeemableWinnings: balance?.redeemableWinnings,
          candyToNextLevel: balance?.candyToNextLevel,
          usedCandy: balance?.usedCandy,
          candy: balance?.candy,
          cashout: balance?.cashout,
          total: balance?.total,
        },
        refreshBalance,
      }}
    >
      {children}
    </BalanceContext.Provider>
  );
};

export const useGetBalance = () => {
  const context = useContext(BalanceContext);
  if (context === undefined) {
    throw new Error(
      'useGetBalance must be used within an BalanceContextProvider'
    );
  }
  return context;
};
// ----------------------------------------------------------------------
